import React from "react";
import { Helmet } from "react-helmet-async";
import {
  FileText,
  SlidersHorizontal,
  ListChecks,
  Trophy,
  Share2,
  UserCircle,
  Search,
  ShieldCheck,
  Star,
} from "lucide-react";
import PageLayout from "../components/layout/PageLayout";

const pasosCompradores = [
  {
    number: 1,
    icon: FileText,
    title: "Crea una licitación",
    description:
      "Completa el asistente de publicación paso a paso: qué necesitas, condiciones, plazos y documentos de referencia.",
  },
  {
    number: 2,
    icon: SlidersHorizontal,
    title: "Define criterios ponderables",
    description:
      "Asigna un peso a cada criterio de evaluación (precio, experiencia, plazos, etc.) para comparar propuestas de forma objetiva.",
  },
  {
    number: 3,
    icon: ListChecks,
    title: "Compara las postulaciones",
    description:
      "Revisa lado a lado todas las propuestas recibidas, con el puntaje calculado según tus criterios.",
  },
  {
    number: 4,
    icon: Trophy,
    title: "Elige un ganador",
    description:
      "Selecciona la propuesta ganadora. La decisión y el historial de la licitación quedan registrados para futuras referencias.",
  },
  {
    number: 5,
    icon: Share2,
    title: "Invita a tus proveedores de confianza",
    description:
      "Comparte el enlace de tu licitación directamente con los proveedores que quieres que participen, además de dejarla abierta al catálogo público.",
  },
];

const pasosProveedores = [
  {
    number: 1,
    icon: UserCircle,
    title: "Crea y completa tu perfil",
    description:
      "Registra tu empresa con los datos básicos y completa servicios, zonas de cobertura, descripción y fotos para que los compradores te encuentren.",
  },
  {
    number: 2,
    icon: Search,
    title: "Busca y postula a licitaciones",
    description:
      "Explora las licitaciones abiertas en el buscador y envía tu propuesta con los documentos que te pidan.",
  },
  {
    number: 3,
    icon: ShieldCheck,
    title: "Qué significa estar certificado",
    description:
      "La certificación Procu es una evaluación propia de la plataforma que distingue a los proveedores confiables con un sello visible en su perfil y en el catálogo. Es gratuita en esta etapa: escríbenos desde tu perfil para iniciar el proceso.",
  },
  {
    number: 4,
    icon: Star,
    title: "Cómo funcionan las reseñas",
    description:
      "Al cerrar una licitación, comprador y proveedor pueden calificarse mutuamente. Estas reseñas quedan visibles en el perfil público de cada empresa.",
  },
];

const PasosSection = ({ titulo, subtitulo, pasos }) => (
  <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
    <div className="text-center">
      <h2 className="text-2xl font-semibold text-foreground">{titulo}</h2>
      <p className="mt-2 text-muted-foreground">{subtitulo}</p>
    </div>
    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {pasos.map((paso) => (
        <div
          key={paso.number}
          className="flex flex-col items-center gap-3 rounded-lg border border-border bg-background p-6 text-center"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <paso.icon className="h-6 w-6" />
          </div>
          <span className="text-sm font-semibold text-primary">Paso {paso.number}</span>
          <h3 className="text-lg font-semibold text-foreground">{paso.title}</h3>
          <p className="text-sm text-muted-foreground">{paso.description}</p>
        </div>
      ))}
    </div>
  </section>
);

const ComoFunciona = () => {
  return (
    <PageLayout>
      <Helmet>
        <title>Cómo funciona Procu</title>
        <meta
          name="description"
          content="Aprende cómo publicar licitaciones, comparar postulaciones y encontrar proveedores certificados en Procu, paso a paso."
        />
        <meta name="keywords" content="cómo funciona, ayuda, Procu, licitaciones, proveedores, certificación" />
      </Helmet>

      <div className="mx-auto max-w-3xl px-4 pt-16 text-center sm:px-6">
        <h1 className="text-3xl font-bold text-foreground">Cómo funciona Procu</h1>
        <p className="mt-2 text-muted-foreground">
          Todo lo que necesitas saber para publicar licitaciones o postular como proveedor.
        </p>
      </div>

      <PasosSection
        titulo="Para compradores"
        subtitulo="De la necesidad al proveedor elegido, sin salir de Procu."
        pasos={pasosCompradores}
      />

      <div className="border-t border-border" />

      <PasosSection
        titulo="Para proveedores"
        subtitulo="Muestra tu empresa y postula a las licitaciones que te interesan."
        pasos={pasosProveedores}
      />
    </PageLayout>
  );
};

export default ComoFunciona;
